import React, { useState } from "react";

import { SortDownIcon as SortDown, SortUpIcon as SortUp } from "@packages/ds";
import { useIntl } from "react-intl";

import * as Dropdown from "ds/Dropdown";
import * as Radio from "ds/Radio";
import Toggle from "ds/Toggle";
import { Tooltip } from "ds/Tooltip";

import * as S from "./ToggleLayout.styles";

const DEFAULT_SORT_TYPES = [
  { id: "name", ascend: "A-Z", descend: "Z-A" },
  { id: "region", ascend: "A-Z", descend: "Z-A" },
  {
    id: "owner",
    ascend: "A-Z",
    descend: "Z-A"
  },
  {
    id: "created_at",
    ascend: "recent last",
    descend: "recent first"
  },
  { id: "plan", ascend: "D-XXL", descend: "XXL-D" },
  { id: "id", ascend: "0 - 1", descend: "1 - 0" }
];

type Props = {
  toggleLayout: (value: boolean) => void;
  gridLayout: boolean;
  sortType: string;
  setSortType: (type: string) => void;
  sortOrder: string;
  setSortOrder: (type: string) => void;
  sortTypes: Array<{ id: string }>;
};

const ToggleLayout = ({
  toggleLayout,
  gridLayout,
  sortType,
  setSortType,
  sortOrder,
  setSortOrder,
  sortTypes = DEFAULT_SORT_TYPES
}: Props) => {
  const intl = useIntl();
  const selected = sortTypes.find(({ id }) => id === sortType) || sortTypes[0];
  const [isOpen, setIsOpen] = useState(false);

  const handleSelectFilter = typeId => {
    setSortType(typeId);
    setIsOpen(false);
  };

  return (
    <S.FiltersWrapper>
      <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
        <S.DropdownTrigger variant="mixed">
          {intl.formatMessage({
            id: `projects.toggle.sort_by.${selected.id}`
          })}
        </S.DropdownTrigger>
        <Dropdown.Portal>
          <Dropdown.Content align="start">
            <Radio.Group defaultValue={selected.id}>
              {sortTypes.map(type => (
                <S.RadioInput
                  key={`sort-${type.id}`}
                  id={type.id}
                  label={
                    <S.RadioLabel>
                      {intl.formatMessage({
                        id: `projects.toggle.sort_by.${type.id}`
                      })}
                      ({type[sortOrder]})
                    </S.RadioLabel>
                  }
                  onClick={() => {
                    handleSelectFilter(type.id);
                  }}
                  value={type.id}
                />
              ))}
            </Radio.Group>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      <Tooltip
        tooltip={intl.formatMessage({ id: "projects.sort.reverse" })}
        analyticId="time_input_tip"
      >
        <Toggle
          toggleType="icon"
          layout="custom"
          data-tip
          data-for="tooltip-reverse-sort"
          onPressedChange={() => {
            setSortOrder(sortOrder === "ascend" ? "descend" : "ascend");
            (document.activeElement as HTMLElement).blur();
          }}
          pressed={false}
          customIcon={sortOrder === "ascend" ? <SortUp /> : <SortDown />}
        />
      </Tooltip>

      <Toggle
        toggleType="icon"
        layout="grid"
        onPressedChange={() => {
          toggleLayout(true);
          (document.activeElement as HTMLElement).blur();
        }}
        pressed={gridLayout}
      />

      <Toggle
        toggleType="icon"
        layout="list"
        onPressedChange={() => {
          toggleLayout(false);
          (document.activeElement as HTMLElement).blur();
        }}
        pressed={!gridLayout}
      />
    </S.FiltersWrapper>
  );
};

export default ToggleLayout;
